import { ErrorReportingUtils } from '@mm/plugins';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import facebookSessionCRUDActions from '@mm/state/actions/FacebookSessionCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { getErrorJson } from '@mm/utils';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import {
  ActionButton,
  ButtonTheme,
} from '../../buttons/ActionButton/ActionButton';
import BasicModalTemplate from '../BasicModalTemplate';
import { ModalInstanceProps } from '../interfaces';
import './GrantPermissionsModal.pcss';

type OuterProps = {
  permissions: string[];
  onCloseCallback: () => any;
  allowManualFlow?: boolean;
  headerText?: string;
  bodyText?: string;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  ModalInstanceProps;

type Props = OuterProps & InnerProps;

const GrantPermissionsModal = (props: Props) => {
  const {
    permissions,
    createFBSession,
    requestPermissions,
    onCloseCallback,
    closeModal,
    headerText,
    bodyText,
    allowManualFlow,
    requestPermissionsWithManualFlowFallback,
  } = props;

  const requestPermissionsAndReload = async () => {
    let facebookToken = '';

    try {
      const response = allowManualFlow
        ? await requestPermissionsWithManualFlowFallback(permissions)
        : await requestPermissions(permissions);
      if (!response || !response.facebookToken) {
        return closeModal();
      }
      facebookToken = response.facebookToken;
      await createFBSession({ facebookToken });

      if (onCloseCallback) {
        onCloseCallback();
      }

      closeModal();
    } catch (error) {
      const extraData: { [key: string]: any } = {
        permissionRequested: permissions,
        facebookRecognitionBadge: facebookToken,
        error,
      };

      if (error instanceof Response) {
        extraData.responseErrorBody = await getErrorJson(error);
      }

      ErrorReportingUtils.reportError(
        'GrantPermissionsModal permissions error',
        { extraData },
      );

      setTimeout(() => {
        location.reload();
      }, 400);
    }
  };

  const title = headerText
    ? headerText
    : 'Some Necessary Permissions are missing';
  const message = bodyText
    ? bodyText
    : " Looks like we're missing some permissions needed to power your messenger marketing";

  return (
    <BasicModalTemplate
      className="grant-permissions-modal"
      hideCloseButton={true}
    >
      <div className="grant-permissions-modal__header-wrapper" />

      <h1 className="grant-permissions-modal__header">{title}</h1>

      <div className="grant-permissions-modal__message">{message}</div>

      <ActionButton
        onClick={requestPermissionsAndReload}
        theme={ButtonTheme.blueOrangeGradient}
        className="grant-permissions-modal__sign-in-button"
      >
        Grant Permissions
      </ActionButton>
    </BasicModalTemplate>
  );
};

const mapStateToProps = (state: StoreState) => ({
  facebookConnectModal: state.network.facebookConnectModal,
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  createFBSession: ({ facebookToken }) =>
    dispatch(facebookSessionCRUDActions.create({ facebookToken })),
  requestPermissions: (permissions: string[]) =>
    dispatch(facebookSDKActions.requestPermissions(permissions)),
  requestPermissionsWithManualFlowFallback: (permissions: string[]) =>
    dispatch(
      facebookSDKActions.requestPermissionsWithManualFlowFallback(permissions),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GrantPermissionsModal);
